import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { VerifyTranscriptDocument } from '@sandtech-hub-monorepo/ehub-client';
import {
  AlertTriangle,
  Clock,
  Download,
  ExternalLink,
  Loader2,
  ShieldCheck,
} from 'lucide-react';
import { FitLogo, REGISTRAR_EMAIL } from '@sandtech-hub-monorepo/ui-kit-site';
import { Button } from '@sandtech-hub-monorepo/ui-kit';

const formatDate = (value: string): string =>
  new Date(value).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

/**
 * Recipient-facing verification portal. It confirms the transcript is authentic
 * and hands over the frozen PDF that was rendered at issue time — it does NOT
 * re-render the record client-side, so what the recipient reads is byte-for-byte
 * the document the registrar approved, immune to any later change in how we
 * render transcripts.
 */
export const TranscriptVerify = () => {
  const { verificationCode } = useParams<{ verificationCode: string }>();

  const { data, loading, error } = useQuery(VerifyTranscriptDocument, {
    variables: { verificationCode: verificationCode ?? '' },
    skip: !verificationCode,
  });

  const result = data?.verifyTranscript;
  const documentUrl = (result?.isValid && result.documentUrl) || null;
  // Attachment-disposition URL for the explicit Download button (documentUrl is
  // inline, opened in a new tab by the "View" button).
  const documentDownloadUrl =
    (result?.isValid && result.documentDownloadUrl) || null;
  const isVerified = !!result?.isValid && !!documentUrl;
  // Live link, but the document is missing (a render that never completed).
  const isMissingDocument = !!result?.isValid && !documentUrl;
  const isExpired = !!result && !result.isValid;

  return (
    <div className="min-h-screen bg-muted/30 flex flex-col">
      <header className="bg-background border-b border-border">
        <div className="max-w-5xl mx-auto w-full px-4 py-3 flex items-center justify-between">
          <FitLogo className="h-10 w-auto" />
          <span className="text-xs text-muted-foreground">
            Official Transcript Verification
          </span>
        </div>
      </header>

      {loading && (
        <main className="flex-1 flex items-center justify-center py-24">
          <div className="flex flex-col items-center gap-3 text-muted-foreground">
            <Loader2 className="w-8 h-8 animate-spin" />
            <p className="text-sm">Verifying transcript…</p>
          </div>
        </main>
      )}

      {!loading && isVerified && result && (
        <main className="flex-1 w-full max-w-5xl mx-auto px-4 py-10 flex flex-col items-center gap-6">
          <div className="max-w-xl w-full bg-background rounded-2xl shadow-sm p-8 flex flex-col items-center gap-5 text-center">
            <div className="p-6 bg-primary/20 rounded-full text-primary">
              <ShieldCheck className="w-12 h-12" />
            </div>

            <div className="flex flex-col gap-1">
              <h1 className="text-xl md:text-2xl font-semibold text-foreground">
                Verified authentic
              </h1>
              <p className="text-muted-foreground">
                This is an official transcript issued by Frontier Institute of
                Technology for{' '}
                <span className="font-semibold text-foreground">
                  {result.studentFullName}
                </span>
                .
              </p>
            </div>

            <dl className="w-full grid grid-cols-1 sm:grid-cols-3 gap-3 border-y border-border py-4 text-left">
              <div>
                <dt className="text-xs text-muted-foreground">Request</dt>
                <dd className="text-sm font-medium text-foreground">
                  #TR-{result.requestNumber}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Issued on</dt>
                <dd className="text-sm font-medium text-foreground">
                  {formatDate(result.issuedAt)}
                </dd>
              </div>
              {result.expiresAt && (
                <div>
                  <dt className="text-xs text-muted-foreground">Valid until</dt>
                  <dd className="text-sm font-medium text-foreground">
                    {formatDate(result.expiresAt)}
                  </dd>
                </div>
              )}
            </dl>

            <div className="flex flex-col sm:flex-row gap-2">
              <Button asChild className="gap-2 w-full sm:w-auto">
                <a href={documentDownloadUrl ?? '#'} download>
                  <Download className="w-4 h-4" />
                  Download official transcript (PDF)
                </a>
              </Button>
              {/* Reliable fallback for when the embedded iframe renders blank
                  (notably mobile Safari): opens the inline-disposition PDF in
                  the browser's own viewer, in a new tab. */}
              <Button
                asChild
                variant="outline"
                className="gap-2 w-full sm:w-auto"
              >
                <a
                  href={documentUrl ?? '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLink className="w-4 h-4" />
                  View in new tab
                </a>
              </Button>
            </div>

            <p className="text-xs text-muted-foreground">
              The PDF is the document of record. Its contents were frozen when
              the transcript was issued and cannot change.
            </p>
          </div>
        </main>
      )}

      {!loading && isExpired && result && (
        /* Expired (or revoked) link — per design: tell the recipient who to
           ask for a new transcript. */
        <main className="flex-1 flex items-center justify-center py-24 px-4">
          <div className="max-w-xl w-full flex flex-col items-center gap-5 text-center">
            <Clock className="w-12 h-12 text-foreground" />
            <h1 className="text-xl md:text-2xl font-semibold text-foreground">
              #TR-{result.requestNumber} • Transcript has expired
            </h1>
            <p className="text-muted-foreground">
              Transcripts are{' '}
              <span className="font-semibold text-foreground">
                valid for 30 days
              </span>{' '}
              from the date they are sent.
              {result.expiresAt && (
                <>
                  {' '}
                  This transcript expired on{' '}
                  <span className="font-semibold text-foreground">
                    {formatDate(result.expiresAt)}
                  </span>
                  ,
                </>
              )}{' '}
              and is no longer accessible.
            </p>
            <p className="text-muted-foreground">
              To get access, ask{' '}
              <span className="font-semibold text-foreground">
                {result.studentFullName}
              </span>{' '}
              to send a new transcript request, or contact the FIT Registrar at{' '}
              <a
                href={`mailto:${REGISTRAR_EMAIL}`}
                className="font-semibold text-foreground underline"
              >
                {REGISTRAR_EMAIL}
              </a>
            </p>
          </div>
        </main>
      )}

      {!loading && (isMissingDocument || !result) && (
        <main className="flex-1 flex items-center justify-center py-24 px-4">
          <div className="max-w-sm w-full bg-background rounded-2xl shadow-sm p-8 flex flex-col items-center gap-4 text-center">
            <div className="w-14 h-14 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertTriangle className="w-7 h-7 text-destructive" />
            </div>
            <div>
              <p className="font-bold text-foreground mb-1">
                Transcript unavailable
              </p>
              <p className="text-sm text-muted-foreground">
                {isMissingDocument ? (
                  <>
                    This transcript document could not be loaded. Please contact
                    the FIT Registrar at{' '}
                    <a
                      href={`mailto:${REGISTRAR_EMAIL}`}
                      className="font-semibold text-foreground underline"
                    >
                      {REGISTRAR_EMAIL}
                    </a>
                  </>
                ) : error?.graphQLErrors?.some((e) =>
                    e.message.includes('TRANSCRIPT_NOT_FOUND')
                  ) ? (
                  'This transcript could not be found. The verification link may be invalid.'
                ) : (
                  'An error occurred while verifying this transcript. Please try again later.'
                )}
              </p>
            </div>
          </div>
        </main>
      )}

      <footer className="mt-auto bg-foreground text-background">
        <div className="max-w-5xl mx-auto w-full px-4 py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <p className="text-sm font-semibold">
              Frontier Institute of Technology
            </p>
            <p className="text-xs opacity-80">
              140 E Broadway Ave, #25 Jackson, WY 83001 · +1 (307) 264-2843
            </p>
          </div>
          <p className="text-xs opacity-80">
            Accredited by the Distance Education Accrediting Commission (DEAC)
          </p>
        </div>
      </footer>
    </div>
  );
};
